/**
 * Pagina /tesoreria/recursos — Sprint 15 S15-Z.
 *
 * Mantenedor del tesorero para registrar y mantener el inventario de
 * recursos del barrio (llaveros, controles remotos, citofonia,
 * acceso al wifi, otros). Migra el CRUD que vivia en sa segun la
 * doctrina "recursos es operacion del dia a dia, no tabla maestra".
 *
 * Cobertura S15-Z:
 * - Listar con filtros (casa, tipo, estado) + busqueda.
 * - Crear (delega correlativo automatico al backend; CR es per-casa
 *   CR-<casa>-NN, LL es global LL-NNNN).
 * - Editar campos editables: identificador, residente actual, costo
 *   de reposicion, fecha entrega, disponible_entrega_at, notas. tipo
 *   y casa son inmutables (cambiarlos es "otro recurso").
 * - Borrar fisicamente si no tiene historia. Si la tiene, el backend
 *   responde 409 con motivos y aqui sugerimos /cambiar-estado.
 *
 * Estructura (S15-Z corolario, patron canonico /dev/*):
 * - `RecursosBody`: presentacional. Recibe data + handlers por props.
 *   NO hace fetch ni usa useAuth. El preview `/dev/recursos` lo monta
 *   con mock state.
 * - `RecursosPage`: orquesta los fetch reales y delega al Body.
 *
 * Fuera de scope (sprint dedicado al modelo de stock): tabla
 * stock_recursos separada del recurso asignado, flujo solicitud→cobro
 * →asignacion, vehiculo_id en CR. Ver docs/dev/doctrina-recursos.md.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError } from '../../lib/api/client';
import {
  actualizarRecurso,
  borrarRecurso,
  cambiarEstado,
  listRecursos,
  type RecursoUpdate,
} from '../../lib/api/recursos';
import { listLotes } from '../../lib/api/lotes';
import { listarResidentesDeLote } from '../../lib/api/residentes';
import {
  getStockDisponiblePorTipo,
  listStockRecursos,
} from '../../lib/api/stock-recursos';
import { ModalIngresarStock } from '../../components/tesoreria/ModalIngresarStock';
import { ModalAsignacionDirecta } from '../../components/tesoreria/ModalAsignacionDirecta';
import {
  ModalAsignarRecurso,
  ModalLiberarRecurso,
} from '../../components/tesoreria/ModalesAsignacionRecurso';
import { esRecursoAsignable, MOTIVO_BAJA_LABEL } from '../../lib/recursos';
import type {
  EstadoRecurso,
  EstadoStock,
  LoteResumen,
  MotivoBaja,
  RecursoRead,
  StockRecursoDisponibleResumen,
  StockRecursoRead,
  TipoRecurso,
  TipoStock,
} from '../../lib/api/types';
import type { ResidenteConPersonaRead } from '../../lib/api/residentes';
import { Button } from '../../components/ui/Button';
import { InputFecha } from '../../components/ui/InputFecha';
import { InputMontoCLP } from '../../components/ui/InputMontoCLP';
import { StockView } from '../../components/tesoreria/StockView';
import { PilasView } from '../../components/tesoreria/PilasView';
import { SolicitudesView } from '../../components/tesoreria/SolicitudesView';
import { ModalVenderPilas } from '../../components/tesoreria/ModalVenderPilas';
import { RECURSO_TIPO_LABEL } from '../../lib/labels';
import { Monto, Num } from '../../components/tesoreria/Monto';
import {
  listSolicitudesConDetalle,
  cancelarEntregaSolicitud,
  cancelarSolicitudManual,
  marcarEntregado,
  pagarRecursoConSaldoAFavor,
} from '../../lib/api/solicitudes-recurso';
import type { SolicitudRecursoConDetalle } from '../../lib/api/types';

const ETIQUETA_ESTADO: Record<EstadoRecurso, string> = {
  entregado: 'Entregado',
  devuelto: 'Devuelto',
  perdido: 'Perdido',
  reemplazado: 'Reemplazado',
  reportado: 'Problema reportado',
  de_baja: 'Dado de baja',
};

const COLOR_ESTADO: Record<EstadoRecurso, string> = {
  entregado: 'text-state-success',
  devuelto: 'text-text-muted',
  perdido: 'text-state-danger',
  reemplazado: 'text-text-muted',
  reportado: 'text-state-warning',
  de_baja: 'text-text-muted',
};

const TIPOS: TipoRecurso[] = ['llavero', 'control_remoto', 'otro'];

const ESTADOS_FILTRO: EstadoRecurso[] = [
  'entregado',
  'reportado',
  'de_baja',
  'devuelto',
  'perdido',
  'reemplazado',
];

function mensajeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === 'object' && 'detail' in body) {
      const detail = (body as { detail: unknown }).detail;
      if (typeof detail === 'string') return detail;
      if (
        detail &&
        typeof detail === 'object' &&
        'message' in detail
      ) {
        const msg = (detail as { message: unknown }).message;
        const motivos = (detail as { motivos?: unknown }).motivos;
        const motivosStr = Array.isArray(motivos)
          ? ` Motivos: ${motivos.join('; ')}.`
          : '';
        return `${String(msg)}${motivosStr}`;
      }
    }
    return `Error ${err.status}`;
  }
  return fallback;
}

// ============================================================
// Modales (presentacionales — reciben callbacks)
// ============================================================


interface ModalEditarProps {
  recurso: RecursoRead;
  lotes: LoteResumen[];
  onListarResidentes: (loteId: string) => Promise<ResidenteConPersonaRead[]>;
  onCerrar: () => void;
  onActualizar: (id: string, data: RecursoUpdate) => Promise<string | null>;
}

function ModalEditar({
  recurso,
  lotes,
  onListarResidentes,
  onCerrar,
  onActualizar,
}: ModalEditarProps) {
  const [identificador, setIdentificador] = useState(recurso.identificador);
  // Sprint 17 S17-Z13: state numérico con InputMontoCLP.
  const [costoReposicion, setCostoReposicion] = useState<number | null>(
    recurso.costo_reposicion,
  );
  const [entregadoAt, setEntregadoAt] = useState(recurso.entregado_at);
  const [disponibleAt, setDisponibleAt] = useState(
    recurso.disponible_entrega_at ?? '',
  );
  const [notas, setNotas] = useState(recurso.notas ?? '');
  // Serial RFID: solo aplica a llaveros. Vive en stock_recursos; el PATCH
  // lo enruta. Precargado con el serial actual del llavero (si lo tiene).
  const [serialRfid, setSerialRfid] = useState(recurso.serial_rfid ?? '');
  const [residentes, setResidentes] = useState<ResidenteConPersonaRead[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    onListarResidentes(recurso.lote_id)
      .then((lista) => {
        if (!cancelado) setResidentes(lista);
      })
      .catch(() => {
        if (!cancelado) setResidentes([]);
      });
    return () => {
      cancelado = true;
    };
  }, [recurso.lote_id, onListarResidentes]);

  const costoNum = costoReposicion ?? 0;
  const valido =
    costoNum > 0 &&
    identificador.trim().length > 0 &&
    entregadoAt !== '';
  const portadorActual =
    residentes.find((r) => r.id === recurso.residente_actual_id)
      ?.persona_nombre_completo ?? null;
  const loteIdentificador =
    lotes.find((l) => l.id === recurso.lote_id)?.identificador ?? recurso.lote_id;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    // Solo se envía el serial si es llavero, no está vacío y cambió: así un
    // guardado que no toca el serial no dispara el 422 de "serial requerido".
    const serialTrim = serialRfid.trim();
    const serialCambio =
      recurso.tipo === 'llavero' &&
      serialTrim !== '' &&
      serialTrim !== (recurso.serial_rfid ?? '');
    const err = await onActualizar(recurso.id, {
      identificador: identificador.trim(),
      costo_reposicion: costoNum,
      entregado_at: entregadoAt,
      disponible_entrega_at: disponibleAt || null,
      notas: notas.trim() || null,
      ...(serialCambio ? { serial_rfid: serialTrim } : {}),
    });
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Editar recurso"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-xl w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Editar recurso {recurso.identificador}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="text-sm text-text-secondary space-y-0.5">
          <div>
            Tipo:{' '}
            <span className="text-text-primary font-medium">
              {RECURSO_TIPO_LABEL[recurso.tipo]}
            </span>{' '}
            <span className="text-text-muted">(inmutable)</span>
          </div>
          <div>
            Casa:{' '}
            <span className="text-text-primary font-medium">
              {loteIdentificador}
            </span>{' '}
            <span className="text-text-muted">(inmutable)</span>
          </div>
          <div>
            Estado:{' '}
            <span className={`font-medium ${COLOR_ESTADO[recurso.estado]}`}>
              {ETIQUETA_ESTADO[recurso.estado]}
            </span>{' '}
            <span className="text-text-muted">(via /cambiar-estado)</span>
          </div>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Identificador <span className="text-state-danger">*</span>
          </span>
          <input
            type="text"
            value={identificador}
            onChange={(e) => setIdentificador(e.target.value)}
            maxLength={50}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            required
          />
        </label>

        {recurso.tipo === 'llavero' && (
          <label className="text-sm space-y-1 block">
            <span className="text-text-secondary">
              Serial RFID{' '}
              <span className="text-text-muted">
                (el número grabado en el llavero físico)
              </span>
            </span>
            <input
              type="text"
              value={serialRfid}
              onChange={(e) => setSerialRfid(e.target.value)}
              maxLength={50}
              placeholder="Ej: 0008123456"
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary font-mono"
            />
          </label>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Costo de reposición (CLP){' '}
              <span className="text-state-danger">*</span>
            </span>
            <InputMontoCLP
              value={costoReposicion}
              onChange={setCostoReposicion}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
            />
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">
              Fecha de entrega <span className="text-state-danger">*</span>
            </span>
            <InputFecha
              value={entregadoAt}
              onChange={setEntregadoAt}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              requerido
            />
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">
            Disponible para retiro{' '}
            <span className="text-text-muted">(opcional)</span>
          </span>
          <InputFecha
            value={disponibleAt}
            onChange={setDisponibleAt}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {/* Sprint 22: el portador ya NO se edita acá. Era un `setattr` sin
            validación (permitía asignar a un residente de otra casa, o un
            cuarto llavero a la misma persona) y sin registro de por qué había
            cambiado. Se muestra en lectura; para cambiarlo están las acciones
            "Asignar" y "Liberar" de la fila, que validan los invariantes y
            escriben el historial de asignaciones. */}
        <div className="text-sm space-y-1">
          <span className="text-text-secondary">Portador actual</span>
          <p className="text-text-primary">
            {portadorActual ?? (
              <span className="text-text-muted">— en la bolsa de la casa —</span>
            )}
          </p>
          <p className="text-[11px] text-text-muted">
            Para cambiarlo, usa "Asignar" o "Liberar" en la fila del recurso: así
            queda registrado quién lo portó y por qué dejó de portarlo.
          </p>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={3}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}

interface ConfirmBorrarProps {
  recurso: RecursoRead;
  onCerrar: () => void;
  onBorrar: (id: string) => Promise<string | null>;
}

function ConfirmBorrar({ recurso, onCerrar, onBorrar }: ConfirmBorrarProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmar() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    const err = await onBorrar(recurso.id);
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Borrar recurso"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <h2 className="text-lg font-medium text-text-primary">
          Borrar {recurso.identificador}
        </h2>
        <p className="text-sm text-text-secondary">
          Esta acción es permanente. Sólo se puede borrar si el recurso no
          tiene historia (sin venta registrada, sin reemplazos, sin
          suspensiones y sin distribuciones de pago). Si tiene historia y
          salió de circulación, usa{' '}
          <span className="text-text-primary">Dar de baja</span> en su lugar.
        </p>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={confirmar}
            disabled={submitting}
          >
            {submitting ? 'Borrando…' : 'Sí, borrar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ConfirmDarDeBajaProps {
  recurso: RecursoRead;
  onCerrar: () => void;
  onDarDeBaja: (
    id: string,
    motivoBaja: MotivoBaja,
    detalle: string | null,
  ) => Promise<string | null>;
}

/**
 * Dar de baja un llavero o control, esté entregado (perdido, robado, se lo
 * llevó quien dejó la casa; 2026-10-06) o reportado (S19 cabo #4). La baja lo
 * lleva a estado terminal, libera el cupo de la persona/vehículo y lo deja en
 * "Por desprogramar" (Reprogramación) hasta que se borre de motores y puertas.
 * Motivo de lista cerrada; el detalle es obligatorio solo si es "Otro".
 */
function ConfirmDarDeBaja({
  recurso,
  onCerrar,
  onDarDeBaja,
}: ConfirmDarDeBajaProps) {
  const [motivoBaja, setMotivoBaja] = useState<MotivoBaja | ''>('');
  const [detalle, setDetalle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const esControl = recurso.tipo === 'control_remoto';

  async function confirmar() {
    if (submitting) return;
    if (!motivoBaja) {
      setError('Elige el motivo de la baja.');
      return;
    }
    if (motivoBaja === 'otro' && !detalle.trim()) {
      setError('Describe el motivo de la baja.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const err = await onDarDeBaja(
      recurso.id,
      motivoBaja,
      detalle.trim() || null,
    );
    setSubmitting(false);
    if (err) setError(err);
    else onCerrar();
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Dar de baja recurso"
    >
      <div className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-md w-full space-y-4">
        <h2 className="text-lg font-medium text-text-primary">
          Dar de baja {recurso.identificador}
        </h2>
        <p className="text-sm text-text-secondary">
          El {esControl ? 'control' : 'llavero'} sale de circulación y libera
          el cupo {esControl ? 'del vehículo' : 'de la persona'}. Queda en{' '}
          <strong>Reprogramación → Por desprogramar</strong> hasta que lo
          borres de los motores y puertas. La acción no se revierte.
        </p>
        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Motivo <span className="text-state-danger">*</span>
          </span>
          <select
            aria-label="Motivo de la baja"
            value={motivoBaja}
            onChange={(e) => setMotivoBaja(e.target.value as MotivoBaja | '')}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm px-2 py-1.5 text-sm text-text-primary"
          >
            <option value="">— elige —</option>
            {(Object.keys(MOTIVO_BAJA_LABEL) as MotivoBaja[]).map((m) => (
              <option key={m} value={m}>
                {MOTIVO_BAJA_LABEL[m]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm space-y-1">
          <span className="text-text-secondary">
            Detalle{' '}
            {motivoBaja === 'otro' ? (
              <span className="text-state-danger">*</span>
            ) : (
              <span className="text-text-muted">(opcional)</span>
            )}
          </span>
          <textarea
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            rows={3}
            maxLength={1000}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded-sm p-2 text-sm text-text-primary"
            placeholder="Ej: el arrendatario dejó la casa en septiembre."
          />
        </label>
        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onCerrar} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmar} disabled={submitting}>
            {submitting ? 'Dando de baja…' : 'Sí, dar de baja'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Body presentacional
// ============================================================

/**
 * Sprint 17 S17-12 — tabs internos del mantenedor:
 * - inventario: la vista historica (recursos asignados a casas).
 * - solicitudes: cola de solicitudes en distintos estados (S17-12 fase 2,
 *   por ahora placeholder).
 * - stock: items disponibles para asignar (S17-13 lo llena, por ahora
 *   placeholder).
 */
export type RecursosTab = 'inventario' | 'solicitudes' | 'stock' | 'pilas';

export interface RecursosBodyProps {
  tab: RecursosTab;
  onTabChange: (tab: RecursosTab) => void;
  recursos: RecursoRead[];
  lotes: LoteResumen[];
  loading: boolean;
  error: string | null;
  onActualizar: (id: string, data: RecursoUpdate) => Promise<string | null>;
  onBorrar: (id: string) => Promise<string | null>;
  // Baja de un recurso entregado o reportado (motivo de lista cerrada).
  onDarDeBaja: (
    id: string,
    motivoBaja: MotivoBaja,
    detalle: string | null,
  ) => Promise<string | null>;
  onListarResidentes: (loteId: string) => Promise<ResidenteConPersonaRead[]>;
  /** Sprint 22 — recargar el listado tras asignar o liberar un recurso.
   *  Opcional para que los previews dev-only y los tests de render no tengan
   *  que cablearla (mismo criterio que `solicitudesPorEntregar`). */
  onAsignacionCambiada?: () => void;
  // Sprint 17 S17-13 — Stock
  stockItems: StockRecursoRead[];
  stockResumen: StockRecursoDisponibleResumen[];
  stockLoading: boolean;
  stockError: string | null;
  stockFiltroTipo: TipoStock | 'todos';
  stockFiltroEstado: EstadoStock | 'todos';
  onStockFiltroTipoChange: (t: TipoStock | 'todos') => void;
  onStockFiltroEstadoChange: (e: EstadoStock | 'todos') => void;
  onIngresarStock: () => void;
  onAsignacionDirecta: () => void;
  // Pilas (Sprint 19a RP-04 — venta en un solo paso)
  pilasStock: StockRecursoRead[];
  pilasVentasRecientes: StockRecursoRead[];
  pilasLoading: boolean;
  pilasError: string | null;
  onVenderPilas: () => void;
  // S17-12 Fase 2 — Solicitudes
  solicitudesEsperandoCobro: SolicitudRecursoConDetalle[];
  // 2026-06: solicitudes pagadas/validadas listas para entrega física.
  solicitudesPorEntregar?: SolicitudRecursoConDetalle[];
  solicitudesAsignadasRecientes: SolicitudRecursoConDetalle[];
  solicitudesHistorico: SolicitudRecursoConDetalle[];
  solicitudesLoading: boolean;
  solicitudesError: string | null;
  // C5: anular cobro de una solicitud en esperando_cobro. Devuelve mensaje
  // de error o null si fue OK. Opcional: la página real siempre lo pasa; el
  // preview/tests pueden omitirlo (la acción no se muestra).
  onAnularSolicitud?: (id: string, motivo: string) => Promise<string | null>;
  // 2026-06: cobrar una solicitud en esperando_cobro con el saldo a favor
  // de la casa (sin pago nuevo). Devuelve mensaje de error o null si fue OK.
  onPagarSaldoSolicitud?: (id: string) => Promise<string | null>;
  // 2026-06: marcar entregado (entrega física) una solicitud en
  // esperando_retiro. Devuelve mensaje de error o null si fue OK.
  onEntregarSolicitud?: (id: string, stockId: string) => Promise<string | null>;
  // RP-05: cancelar una entrega en esperando_retiro (con destino del dinero).
  onCancelarEntregaSolicitud?: (
    id: string,
    motivo: string,
    destino: 'saldo_a_favor' | 'reembolso',
  ) => Promise<string | null>;
}

export function RecursosBody({
  tab,
  onTabChange,
  recursos,
  lotes,
  loading,
  error,
  onActualizar,
  onBorrar,
  onDarDeBaja,
  onListarResidentes,
  onAsignacionCambiada = () => undefined,
  stockItems,
  stockResumen,
  stockLoading,
  stockError,
  stockFiltroTipo,
  stockFiltroEstado,
  onStockFiltroTipoChange,
  onStockFiltroEstadoChange,
  onIngresarStock,
  onAsignacionDirecta,
  pilasStock,
  pilasVentasRecientes,
  pilasLoading,
  pilasError,
  onVenderPilas,
  solicitudesEsperandoCobro,
  solicitudesAsignadasRecientes,
  solicitudesPorEntregar = [],
  solicitudesHistorico,
  solicitudesLoading,
  solicitudesError,
  onAnularSolicitud,
  onPagarSaldoSolicitud,
  onEntregarSolicitud,
  onCancelarEntregaSolicitud,
}: RecursosBodyProps) {
  const [filtroLote, setFiltroLote] = useState<string>('');
  const [filtroTipo, setFiltroTipo] = useState<TipoRecurso | 'todos'>('todos');
  const [filtroEstado, setFiltroEstado] = useState<EstadoRecurso | 'todos'>(
    'todos',
  );
  const [search, setSearch] = useState('');
  // Z18 — state mostrarCrear eliminado junto con el botón "+ Nuevo recurso".
  const [editando, setEditando] = useState<RecursoRead | null>(null);
  const [borrando, setBorrando] = useState<RecursoRead | null>(null);
  const [dandoDeBaja, setDandoDeBaja] = useState<RecursoRead | null>(null);
  // Sprint 22 — asignar/liberar como operaciones explícitas con historial.
  const [asignando, setAsignando] = useState<RecursoRead | null>(null);
  const [liberando, setLiberando] = useState<RecursoRead | null>(null);

  const lotesPorId = useMemo(() => {
    const map: Record<string, LoteResumen> = {};
    for (const l of lotes) map[l.id] = l;
    return map;
  }, [lotes]);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recursos.filter((r) => {
      if (filtroLote && r.lote_id !== filtroLote) return false;
      if (filtroTipo !== 'todos' && r.tipo !== filtroTipo) return false;
      if (filtroEstado !== 'todos' && r.estado !== filtroEstado) return false;
      if (q) {
        const lote = lotesPorId[r.lote_id];
        const hay = `${r.identificador} ${lote?.identificador ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [recursos, filtroLote, filtroTipo, filtroEstado, search, lotesPorId]);

  const tabs: { id: RecursosTab; label: string }[] = [
    { id: 'inventario', label: 'Inventario' },
    { id: 'solicitudes', label: 'Solicitudes' },
    { id: 'stock', label: 'Stock' },
    { id: 'pilas', label: 'Pilas' },
  ];

  return (
    <div className="px-4 sm:px-8 py-6 space-y-6">
      {/* Header fijo arriba de los tabs */}
      <div>
        <div className="text-xs uppercase tracking-wide text-orange font-medium">
          Tesorería
        </div>
        <h1 className="text-2xl font-medium text-text-primary mt-1">
          Recursos
        </h1>
        <p className="text-sm text-text-muted mt-1 max-w-3xl">
          Llaveros, controles remotos, citofonía, accesos al Wi-Fi y pilas
          del barrio. Tres vistas: el inventario asignado a cada casa, la
          cola de solicitudes pendientes y el stock disponible para asignar.
        </p>
      </div>

      {/* Sprint 17 S17-12 — Tabs internos */}
      <div
        className="flex gap-1 -mb-3 overflow-x-auto flex-nowrap border-b border-white/[0.08]"
        role="tablist"
        aria-label="Vistas de recursos"
      >
        {tabs.map((t) => {
          const activo = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activo}
              onClick={() => onTabChange(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activo
                  ? 'text-orange border-orange'
                  : 'text-text-secondary border-transparent hover:text-text-primary'
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* === Tab: Inventario === */}
      {tab === 'inventario' && (
        <>
      <div className="flex flex-col gap-3">
        <div className="text-sm text-text-muted">
          Recursos asignados a las casas del barrio. Aquí se ajustan datos y
          se borran los creados por error (sin historia).
        </div>
        {/* Z18 — eliminamos "+ Nuevo recurso" (flujo viejo S15-Z fuera de
            doctrina S17). Los recursos nacen al asignar desde Stock o tras
            cobrar una solicitud aprobada por Comisión. */}
        <div className="bg-state-info/[0.08] border-l-4 border-state-info p-3 rounded-sm text-xs text-text-secondary">
          Los recursos se crean automáticamente al{' '}
          <a
            href="/tesoreria/recursos?tab=stock"
            className="text-orange underline hover:text-orange/80"
          >
            asignar desde Stock
          </a>{' '}
          (Asignación Directa) o tras el pago de una solicitud aprobada por
          la Comisión. No se crean manualmente desde aquí.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar identificador o casa…"
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary w-full sm:w-72"
        />
        <select
          value={filtroLote}
          onChange={(e) => setFiltroLote(e.target.value)}
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por casa"
        >
          <option value="">Todas las casas</option>
          {lotes.map((l) => (
            <option
              key={l.id}
              value={l.id}
              className="bg-bg-sunken text-text-primary"
            >
              {l.identificador}
            </option>
          ))}
        </select>
        <select
          value={filtroTipo}
          onChange={(e) =>
            setFiltroTipo(e.target.value as TipoRecurso | 'todos')
          }
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por tipo"
        >
          <option value="todos">Todos los tipos</option>
          {TIPOS.map((t) => (
            <option
              key={t}
              value={t}
              className="bg-bg-sunken text-text-primary"
            >
              {RECURSO_TIPO_LABEL[t]}
            </option>
          ))}
        </select>
        <select
          value={filtroEstado}
          onChange={(e) =>
            setFiltroEstado(e.target.value as EstadoRecurso | 'todos')
          }
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por estado"
        >
          <option value="todos">Todos los estados</option>
          {ESTADOS_FILTRO.map((e) => (
            <option
              key={e}
              value={e}
              className="bg-bg-sunken text-text-primary"
            >
              {ETIQUETA_ESTADO[e]}
            </option>
          ))}
        </select>
        <span className="text-xs text-text-muted">
          {filtrados.length} de {recursos.length}
        </span>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {error}
        </div>
      )}

      {!loading && !error && filtrados.length === 0 && (
        <p className="text-sm text-text-muted">
          {recursos.length === 0
            ? 'No hay recursos cargados. Los recursos nacen al asignar desde Stock o tras cobrar una solicitud aprobada.'
            : 'No hay recursos que coincidan con el filtro.'}
        </p>
      )}

      {!loading && !error && filtrados.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-text-secondary border-b border-white/[0.08]">
              <tr>
                <th className="text-left px-2 py-2 font-medium">
                  Identificador
                </th>
                <th className="text-left px-2 py-2 font-medium">Tipo</th>
                <th className="text-left px-2 py-2 font-medium">Casa</th>
                <th className="text-left px-2 py-2 font-medium">Estado</th>
                <th className="text-right px-2 py-2 font-medium">
                  Costo reposición
                </th>
                <th className="text-left px-2 py-2 font-medium">Entregado</th>
                <th className="text-left px-2 py-2 font-medium">Portador</th>
                <th className="text-right px-2 py-2 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((r) => {
                const lote = lotesPorId[r.lote_id];
                return (
                  <tr
                    key={r.id}
                    data-testid="fila-recurso"
                    className="border-b border-white/[0.04] hover:bg-bg-card/40"
                  >
                    <td className="px-2 py-2 text-text-primary">
                      <Num>{r.identificador}</Num>
                    </td>
                    <td className="px-2 py-2 text-text-secondary">
                      {RECURSO_TIPO_LABEL[r.tipo]}
                    </td>
                    <td className="px-2 py-2 text-text-primary">
                      {lote?.identificador ?? '—'}
                      {lote && (
                        <span className="text-text-muted text-xs ml-1">
                          ({lote.sector})
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-2 py-2 font-medium ${COLOR_ESTADO[r.estado]}`}
                    >
                      {ETIQUETA_ESTADO[r.estado]}
                    </td>
                    <td className="px-2 py-2 text-right text-text-primary">
                      <Monto value={r.costo_reposicion} />
                    </td>
                    <td className="px-2 py-2 text-text-secondary">
                      {r.entregado_at}
                    </td>
                    {/* Sprint 22 — el portador se ve en la tabla y se cambia
                        con acciones explícitas, no editando un campo. */}
                    <td className="px-2 py-2 text-text-secondary">
                      {r.residente_actual_id ? (
                        <span className="text-text-primary">asignado</span>
                      ) : (
                        <span className="text-text-muted">en la bolsa</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => setEditando(r)}
                        className="text-state-info hover:underline"
                      >
                        Editar
                      </button>
                      {esRecursoAsignable(r) &&
                        (r.residente_actual_id === null ? (
                          <button
                            type="button"
                            onClick={() => setAsignando(r)}
                            className="text-state-success hover:underline"
                          >
                            Asignar
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setLiberando(r)}
                            className="text-state-warning hover:underline"
                          >
                            Liberar
                          </button>
                        ))}
                      {(r.estado === 'entregado' ||
                        r.estado === 'reportado') && (
                        <button
                          type="button"
                          onClick={() => setDandoDeBaja(r)}
                          className="text-state-warning hover:underline"
                        >
                          Dar de baja
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setBorrando(r)}
                        className="text-state-danger hover:underline"
                      >
                        Borrar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Z18 — ModalCrear eliminado del render (botón "+ Nuevo recurso"
          oculto). El componente ModalCrear queda definido pero no se
          monta — se borrará completamente en S17-22a junto con onCrear
          y POST /recursos/ legacy. */}

      {editando && (
        <ModalEditar
          recurso={editando}
          lotes={lotes}
          onListarResidentes={onListarResidentes}
          onCerrar={() => setEditando(null)}
          onActualizar={onActualizar}
        />
      )}

      {asignando && (
        <ModalAsignarRecurso
          recurso={asignando}
          onListarResidentes={onListarResidentes}
          onCerrar={() => setAsignando(null)}
          onAsignado={() => {
            setAsignando(null);
            onAsignacionCambiada();
          }}
        />
      )}

      {liberando && (
        <ModalLiberarRecurso
          recurso={liberando}
          onCerrar={() => setLiberando(null)}
          onLiberado={() => {
            setLiberando(null);
            onAsignacionCambiada();
          }}
        />
      )}

      {borrando && (
        <ConfirmBorrar
          recurso={borrando}
          onCerrar={() => setBorrando(null)}
          onBorrar={onBorrar}
        />
      )}

      {dandoDeBaja && (
        <ConfirmDarDeBaja
          recurso={dandoDeBaja}
          onCerrar={() => setDandoDeBaja(null)}
          onDarDeBaja={onDarDeBaja}
        />
      )}
        </>
      )}

      {/* === Tab: Solicitudes (S17-12 Fase 2) === */}
      {tab === 'solicitudes' && (
        <SolicitudesView
          esperandoCobro={solicitudesEsperandoCobro}
          porEntregar={solicitudesPorEntregar}
          asignadasRecientes={solicitudesAsignadasRecientes}
          historico={solicitudesHistorico}
          loading={solicitudesLoading}
          error={solicitudesError}
          onAnular={onAnularSolicitud}
          onPagarSaldo={onPagarSaldoSolicitud}
          onEntregar={onEntregarSolicitud}
          onCancelarEntrega={onCancelarEntregaSolicitud}
        />
      )}

      {/* === Tab: Stock (S17-13 + S17-13b asignación directa) === */}
      {tab === 'stock' && (
        <StockView
          items={stockItems}
          resumen={stockResumen}
          loading={stockLoading}
          error={stockError}
          filtroTipo={stockFiltroTipo}
          filtroEstado={stockFiltroEstado}
          onFiltroTipoChange={onStockFiltroTipoChange}
          onFiltroEstadoChange={onStockFiltroEstadoChange}
          onIngresar={onIngresarStock}
          onAsignacionDirecta={onAsignacionDirecta}
        />
      )}

      {/* === Tab: Pilas (S17-14) === */}
      {tab === 'pilas' && (
        <PilasView
          stockPila={pilasStock}
          ventasRecientes={pilasVentasRecientes}
          loading={pilasLoading}
          error={pilasError}
          onVenderPilas={onVenderPilas}
        />
      )}
    </div>
  );
}

// ============================================================
// Container — fetch real al backend
// ============================================================

export function RecursosPage() {
  const [recursos, setRecursos] = useState<RecursoRead[]>([]);
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // S17-12 / S17-15: estado local del tab activo. Default 'inventario',
  // pero permite entrar a un tab específico vía query param ?tab=X
  // (S17-15: card del dashboard linkea con ?tab=solicitudes).
  const [searchParams] = useSearchParams();
  const tabInicial = (() => {
    const q = searchParams.get('tab');
    return q === 'solicitudes' || q === 'stock' || q === 'pilas'
      ? (q as RecursosTab)
      : 'inventario';
  })();
  const [tab, setTab] = useState<RecursosTab>(tabInicial);

  // S17-13: estado del tab Stock.
  const [stockItems, setStockItems] = useState<StockRecursoRead[]>([]);
  const [stockResumen, setStockResumen] = useState<
    StockRecursoDisponibleResumen[]
  >([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState<string | null>(null);
  const [stockFiltroTipo, setStockFiltroTipo] = useState<TipoStock | 'todos'>(
    'todos',
  );
  // Sprint 17 S17-Z10: vista agrupada por compra. El default es
  // 'todos' porque la agrupación en cliente filtra grupos sin
  // disponibles y el drill-down necesita los asignados/consumidos.
  const [stockFiltroEstado, setStockFiltroEstado] = useState<
    EstadoStock | 'todos'
  >('todos');
  const [stockRefreshKey, setStockRefreshKey] = useState(0);
  const [mostrarIngresar, setMostrarIngresar] = useState(false);
  const [mostrarAsignar, setMostrarAsignar] = useState(false);

  // Estado del tab Pilas (Sprint 19a RP-04 — venta en un solo paso).
  const [pilasStock, setPilasStock] = useState<StockRecursoRead[]>([]);
  const [pilasVentasRecientes, setPilasVentasRecientes] = useState<
    StockRecursoRead[]
  >([]);
  const [pilasLoading, setPilasLoading] = useState(false);
  const [pilasError, setPilasError] = useState<string | null>(null);
  const [pilasRefreshKey, setPilasRefreshKey] = useState(0);
  const [mostrarVentaPila, setMostrarVentaPila] = useState(false);

  // Tab Solicitudes (S17-12 Fase 2): listado enriquecido particionado.
  const [solicitudes, setSolicitudes] = useState<
    SolicitudRecursoConDetalle[]
  >([]);
  const [solicitudesLoading, setSolicitudesLoading] = useState(false);
  const [solicitudesError, setSolicitudesError] = useState<string | null>(null);
  // C5: contador para re-fetch de solicitudes tras anular un cobro.
  const [solicitudesRefresh, setSolicitudesRefresh] = useState(0);

  async function handleAnularSolicitud(
    id: string,
    motivo: string,
  ): Promise<string | null> {
    try {
      await cancelarSolicitudManual(id, motivo);
      setSolicitudesRefresh((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error al anular el cobro');
    }
  }

  async function handleEntregarSolicitud(
    id: string,
    stockId: string,
  ): Promise<string | null> {
    try {
      await marcarEntregado(id, stockId);
      setSolicitudesRefresh((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error al marcar como entregado');
    }
  }

  async function handlePagarSaldoSolicitud(
    id: string,
  ): Promise<string | null> {
    try {
      await pagarRecursoConSaldoAFavor(id);
      setSolicitudesRefresh((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error al pagar con saldo a favor');
    }
  }

  async function handleCancelarEntrega(
    id: string,
    motivo: string,
    destino: 'saldo_a_favor' | 'reembolso',
  ): Promise<string | null> {
    try {
      await cancelarEntregaSolicitud(id, motivo, destino);
      setSolicitudesRefresh((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error al cancelar la entrega');
    }
  }

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    Promise.all([listRecursos({}), listLotes({ activo: true })])
      .then(([rs, ls]) => {
        if (cancelado) return;
        setRecursos(rs);
        setLotes(ls);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar datos`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  async function handleActualizar(
    id: string,
    data: RecursoUpdate,
  ): Promise<string | null> {
    try {
      await actualizarRecurso(id, data);
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error de red');
    }
  }

  async function handleBorrar(id: string): Promise<string | null> {
    try {
      await borrarRecurso(id);
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error de red');
    }
  }

  // Dar de baja un recurso entregado o reportado. Libera el cupo y lo deja
  // en "Por desprogramar" (Reprogramación).
  async function handleDarDeBaja(
    id: string,
    motivoBaja: MotivoBaja,
    detalle: string | null,
  ): Promise<string | null> {
    try {
      await cambiarEstado(id, {
        nuevo_estado: 'de_baja',
        motivo_baja: motivoBaja,
        motivo: detalle ?? undefined,
      });
      setRefreshKey((k) => k + 1);
      return null;
    } catch (err) {
      return mensajeError(err, 'Error de red');
    }
  }

  // Tab Solicitudes: fetch al entrar al tab.
  useEffect(() => {
    if (tab !== 'solicitudes') return;
    let cancelado = false;
    setSolicitudesLoading(true);
    setSolicitudesError(null);
    listSolicitudesConDetalle()
      .then((data) => {
        if (cancelado) return;
        setSolicitudes(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setSolicitudesError(mensajeError(err, 'Error al cargar solicitudes'));
      })
      .finally(() => {
        if (!cancelado) setSolicitudesLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [tab, solicitudesRefresh]);

  // Particiones de solicitudes para SolicitudesView (sin re-fetch).
  const haceTreintaDias = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString();
  }, []);
  const solicitudesEsperandoCobro = useMemo(
    () => solicitudes.filter((s) => s.estado === 'esperando_cobro'),
    [solicitudes],
  );
  const solicitudesPorEntregar = useMemo(
    () => solicitudes.filter((s) => s.estado === 'esperando_retiro'),
    [solicitudes],
  );
  const solicitudesAsignadasRecientes = useMemo(
    () =>
      solicitudes.filter(
        (s) => s.estado === 'asignada' && s.solicitado_at >= haceTreintaDias,
      ),
    [solicitudes, haceTreintaDias],
  );
  const ESTADOS_HISTORICO = ['rechazada', 'cancelada_por_cascada', 'cancelada_manual'];
  const solicitudesHistorico = useMemo(
    () => solicitudes.filter((s) => ESTADOS_HISTORICO.includes(s.estado)),
    [solicitudes],
  );

  // Sprint 19a RP-04 — fetch del tab Pilas: stock en_stock + ventas recientes
  // (pilas consumido, top 20 por consumido_at desc). Ya NO se filtra por texto
  // de descripción: la venta es de un solo paso y el vínculo es por FK.
  useEffect(() => {
    if (tab !== 'pilas') return;
    let cancelado = false;
    setPilasLoading(true);
    setPilasError(null);
    Promise.all([
      listStockRecursos({ tipo: 'pila', estado: 'en_stock' }),
      listStockRecursos({ tipo: 'pila', estado: 'consumido' }),
    ])
      .then(([enStock, vendidas]) => {
        if (cancelado) return;
        setPilasStock(enStock);
        const ordenadas = [...vendidas]
          .sort((a, b) =>
            (b.consumido_at ?? '').localeCompare(a.consumido_at ?? ''),
          )
          .slice(0, 20);
        setPilasVentasRecientes(ordenadas);
      })
      .catch((err) => {
        if (cancelado) return;
        setPilasError(mensajeError(err, 'Error al cargar pilas'));
      })
      .finally(() => {
        if (!cancelado) setPilasLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [tab, pilasRefreshKey]);

  // S17-13 — fetch del stock cuando se entra al tab o cambian filtros.
  useEffect(() => {
    // Sprint 17 Z21-bugfix: el resumen disponible por tipo debe
    // cargarse tambien en tab 'pilas' porque el ModalVenderPilas
    // lo consume. Antes solo se cargaba en tab 'stock' -> el modal
    // mostraba "Stock disponible: 0" aunque hubiera pilas en BD.
    if (tab !== 'stock' && tab !== 'pilas') return;
    let cancelado = false;
    setStockLoading(true);
    setStockError(null);
    const filtros = {
      tipo: stockFiltroTipo !== 'todos' ? stockFiltroTipo : undefined,
      estado: stockFiltroEstado !== 'todos' ? stockFiltroEstado : undefined,
    };
    Promise.all([
      listStockRecursos(filtros),
      getStockDisponiblePorTipo(),
    ])
      .then(([items, resumen]) => {
        if (cancelado) return;
        setStockItems(items);
        setStockResumen(resumen);
      })
      .catch((err) => {
        if (cancelado) return;
        setStockError(mensajeError(err, 'Error al cargar el stock'));
      })
      .finally(() => {
        if (!cancelado) setStockLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [tab, stockFiltroTipo, stockFiltroEstado, stockRefreshKey]);

  return (
    <>
    <RecursosBody
      tab={tab}
      onTabChange={setTab}
      recursos={recursos}
      lotes={lotes}
      loading={loading}
      error={error}
      onActualizar={handleActualizar}
      onBorrar={handleBorrar}
      onDarDeBaja={handleDarDeBaja}
      onListarResidentes={listarResidentesDeLote}
      onAsignacionCambiada={() => setRefreshKey((k) => k + 1)}
      stockItems={stockItems}
      stockResumen={stockResumen}
      stockLoading={stockLoading}
      stockError={stockError}
      stockFiltroTipo={stockFiltroTipo}
      stockFiltroEstado={stockFiltroEstado}
      onStockFiltroTipoChange={setStockFiltroTipo}
      onStockFiltroEstadoChange={setStockFiltroEstado}
      onIngresarStock={() => setMostrarIngresar(true)}
      onAsignacionDirecta={() => setMostrarAsignar(true)}
      pilasStock={pilasStock}
      pilasVentasRecientes={pilasVentasRecientes}
      pilasLoading={pilasLoading}
      pilasError={pilasError}
      onVenderPilas={() => setMostrarVentaPila(true)}
      solicitudesEsperandoCobro={solicitudesEsperandoCobro}
      solicitudesPorEntregar={solicitudesPorEntregar}
      solicitudesAsignadasRecientes={solicitudesAsignadasRecientes}
      solicitudesHistorico={solicitudesHistorico}
      solicitudesLoading={solicitudesLoading}
      solicitudesError={solicitudesError}
      onAnularSolicitud={handleAnularSolicitud}
      onPagarSaldoSolicitud={handlePagarSaldoSolicitud}
      onEntregarSolicitud={handleEntregarSolicitud}
      onCancelarEntregaSolicitud={handleCancelarEntrega}
    />

    {mostrarIngresar && (
      <ModalIngresarStock
        onCerrar={() => setMostrarIngresar(false)}
        onIngresado={() => {
          setMostrarIngresar(false);
          setStockRefreshKey((k) => k + 1);
        }}
      />
    )}

    {mostrarAsignar && (
      <ModalAsignacionDirecta
        lotes={lotes}
        stockResumen={stockResumen}
        onCerrar={() => setMostrarAsignar(false)}
        onCreada={() => {
          setMostrarAsignar(false);
          // No bumpeamos stockRefreshKey: la asignación NO consume stock
          // todavía (eso lo hace S17-06 al clasificar el pago). Solo crea
          // IngresoVario + SolicitudRecurso sintética.
        }}
      />
    )}

    {mostrarVentaPila && (
      <ModalVenderPilas
        lotes={lotes}
        stockResumen={stockResumen}
        onCerrar={() => setMostrarVentaPila(false)}
        onVendida={() => {
          setMostrarVentaPila(false);
          // Vender descuenta stock: refrescar pilas Y el resumen de stock.
          setPilasRefreshKey((k) => k + 1);
          setStockRefreshKey((k) => k + 1);
        }}
      />
    )}
    </>
  );
}
